import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { GhostIcon, PumpkinIcon } from './HalloweekLink';
type Kind = 'ghost' | 'pumpkin';
type Side = 'left' | 'right';
interface Peeker {
  id: number;
  kind: Kind;
  side: Side;
  top: number;
  tilt: number;
}
interface HalloweekPeekersProps {
  onOpen: () => void;
}
const SLOTS: {
  side: Side;
  top: number;
}[] = [{
  side: 'left',
  top: 40
}, {
  side: 'right',
  top: 90
}, {
  side: 'left',
  top: 210
}, {
  side: 'right',
  top: 260
}, {
  side: 'left',
  top: 380
}, {
  side: 'right',
  top: 420
}];
const LINES = ['Bu!', 'Klicka mig!', 'Halloweek →', 'Vågar du?'];
const ease = [0.23, 1, 0.32, 1] as const;
const VISIBLE_MS = 3400;
const SPAWN_MS = 1700;
const MAX_ON_SCREEN = 2;
export function HalloweekPeekers({
  onOpen
}: HalloweekPeekersProps) {
  const reduceMotion = useReducedMotion();
  const [peekers, setPeekers] = useState<Peeker[]>([]);
  const [hovered, setHovered] = useState<number | null>(null);
  const [caught, setCaught] = useState<number | null>(null);
  const nextId = useRef(0);
  const lineFor = (id: number) => LINES[id % LINES.length];
  useEffect(() => {
    if (reduceMotion) return;
    const timeouts: number[] = [];
    const spawn = () => {
      setPeekers(current => {
        if (current.length >= MAX_ON_SCREEN) return current;
        const taken = new Set(current.map(p => `${p.side}-${p.top}`));
        const free = SLOTS.filter(s => !taken.has(`${s.side}-${s.top}`));
        const slot = free[Math.floor(Math.random() * free.length)];
        const id = nextId.current++;
        const peeker: Peeker = {
          id,
          ...slot,
          kind: Math.random() > 0.5 ? 'ghost' : 'pumpkin',
          tilt: Math.round(Math.random() * 16 - 8)
        };
        timeouts.push(window.setTimeout(() => setPeekers(list => list.filter(p => p.id !== id)), VISIBLE_MS));
        return [...current, peeker];
      });
    };
    timeouts.push(window.setTimeout(spawn, 900));
    const interval = window.setInterval(spawn, SPAWN_MS);
    return () => {
      window.clearInterval(interval);
      timeouts.forEach(t => window.clearTimeout(t));
    };
  }, [reduceMotion]);
  const handleCatch = (id: number) => {
    setCaught(id);
    window.setTimeout(onOpen, 220);
  };

  // Reduced motion: one calm, static ghost that still invites a click
  if (reduceMotion) {
    return <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] z-20" data-id="element-583" data-mp="4rtln055qbls">
        <button onClick={onOpen} className="pointer-events-auto absolute right-2 md:right-[6%] top-10 flex flex-col items-center" aria-label="Till Halloweek" data-id="element-584" data-mp="9qog4zchlolq">
          <GhostIcon className="w-12 h-12 md:w-16 md:h-16" data-id="element-585" data-mp="3w2249dh3jvuw" />
        </button>
      </div>;
  }
  return <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] z-20 overflow-hidden" aria-hidden="false" data-id="element-586" data-mp="2fo27gbdjsmbv">
      <AnimatePresence data-id="element-587" data-mp="12me7g7jas4jr">
        {peekers.map(p => {
        const fromX = p.side === 'left' ? -90 : 90;
        const isCaught = caught === p.id;
        const showBubble = hovered === p.id && !isCaught;
        return <motion.button key={p.id} type="button" onClick={() => handleCatch(p.id)} onHoverStart={() => setHovered(p.id)} onHoverEnd={() => setHovered(h => h === p.id ? null : h)} aria-label="Fånga spöket – till Halloweek" initial={{
          x: fromX,
          opacity: 0,
          rotate: p.tilt * 2
        }} animate={isCaught ? {
          scale: 1.35,
          opacity: 0,
          rotate: 0,
          transition: {
            duration: 0.2,
            ease
          }
        } : {
          x: 0,
          opacity: 1,
          rotate: p.tilt,
          transition: {
            duration: 0.3,
            ease
          }
        }} exit={{
          x: fromX,
          opacity: 0,
          transition: {
            duration: 0.25,
            ease
          }
        }} whileHover={{
          scale: 1.12,
          rotate: 0
        }} whileTap={{
          scale: 0.92
        }} style={{
          top: p.top
        }} className={`pointer-events-auto absolute cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7A1A] rounded-full ${p.side === 'left' ? '-left-3 sm:left-2 md:left-[5%] lg:left-[9%]' : '-right-3 sm:right-2 md:right-[5%] lg:right-[9%]'}`} data-id="element-588" data-mp="1bnc23z7uizpo">
              <motion.span className="block drop-shadow-[0_6px_10px_rgba(0,0,0,0.18)]" animate={{
            y: [0, -7, 0]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-589" data-mp="16irxky23xshs">
                {p.kind === 'ghost' ? <GhostIcon className="w-12 h-12 sm:w-14 sm:h-14 md:w-20 md:h-20" data-id="element-590" data-mp="2w0owb6qwlavg" /> : <PumpkinIcon className="w-11 h-11 sm:w-12 sm:h-12 md:w-16 md:h-16" data-id="element-591" data-mp="2r1lz28h608y1" />}
              </motion.span>

              <AnimatePresence data-id="element-592" data-mp="2m4lw46fm3mb5">
                {showBubble && <motion.span initial={{
              opacity: 0,
              scale: 0.96,
              y: 4
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.96,
              y: 4
            }} transition={{
              duration: 0.15,
              ease
            }} className={`absolute -top-9 whitespace-nowrap rounded-full bg-[#0E0C0B] px-3 py-1 text-xs font-bold text-[#FF9A4D] ${p.side === 'left' ? 'left-1/2' : 'right-1/2'}`} data-id="element-593" data-mp="3jr2r350vhntq">
                    {lineFor(p.id)}
                  </motion.span>}
              </AnimatePresence>
            </motion.button>;
      })}
      </AnimatePresence>
    </div>;
}